import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
    Card, Col, Flex, Row, theme, Typography,
} from 'antd';
import {
    useCallback, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate,
} from 'react-router-dom';

import { projectsMutations } from '@/api';
import {
    ConfirmModal, getActionControlStyleVars, ProjectForm,
} from '@/components';
import { AppRoute } from '@/constants';
import { useBackToOrList } from '@/hooks';

export const ProjectCreatePage = () => {
    const navigate = useNavigate();
    const handleBack = useBackToOrList(AppRoute.Projects);
    const { token } = theme.useToken();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [formData, setFormData] = useState<{
        title: string;
        description?: string | null;
        comment?: string | null;
        regulation?: string | null;
        fields?: Array<{ name: string; value: string }> | null;
    } | null>(null);

    const createMutationOptions = projectsMutations.create();
    const { mutate: createProject, isPending } = useMutation(createMutationOptions);

    const handleSubmit = useCallback((data: {
        title: string;
        description?: string | null;
        comment?: string | null;
        regulation?: string | null;
        fields?: Array<{ name: string; value: string }> | null;
    }) => {
        setFormData(data);
        setConfirmModalOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData) {
            createProject(formData, {
                onSuccess: () => {
                    setConfirmModalOpen(false);
                    setFormData(null);
                    navigate(AppRoute.Projects);
                },
            });
        }
    }, [formData, createProject, navigate]);

    const handleCancel = useCallback(() => {
        handleBack();
    }, [handleBack]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    return (
        <>
            <Helmet>
                <title>Створити проект</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={18} xl={16}>
                    <Card
                        title={
                            <Flex align="center" gap="middle">
                                <Link
                                    to={AppRoute.Projects}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Створити проект
                                </Typography.Title>
                            </Flex>
                        }
                    >
                        <ProjectForm
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isPending}
                            cancelButtonText="Відмінити"
                            cancelTo={AppRoute.Projects}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="create"
                entityType="project"
                entityName={formData?.title}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />
        </>
    );
};
